/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const contentFont = 10;
const pageMargins = [42, 80, 28.8, 25.92];
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 10],
  },
  subheader: {
    fontSize: 16,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  tableExample: {
    margin: [0, 5, 0, 15],
  },
  tableHeader: {
    bold: true,
    fontSize: 13,
    color: 'black',
  },
};

const amountFormatter = (value) => {
  if (value < 0) {
    return `($${value
      ?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
      .substring(2)})`;
  }
  if (value) {
    return value?.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }
  return '$0.00';
};

const numberFormatter = (value) => {
  if (value < 0) {
    return `(${value?.toLocaleString('en-US').substring(1)})`;
  }
  if (value) {
    return value?.toLocaleString('en-US');
  }
  return '0.00';
};
const getHeader = (currentPage, pageCount, data, options) => {
  const { prinDate, requestedBy, reportName } = data;

  const { pageSize, info } = options;
  const {
    firstInfo = '',
    secondInfo = '',
    thirdInfo = '',
    fourthInfo = '',
    fifthInfo = '',
  } = info;

  let widths = [255, 245, 245];
  if (pageSize && pageSize.width) {
    const w = (pageSize.width - 95) / 3;
    widths = [w, w, w];
  }
  const header = {
    alignment: 'center',
    fontSize: 10,
    margin: [42, 20, 28.8, 40],
    style: 'tableExample',
    table: {
      widths: widths,
      body: [
        [
          {
            text: [{ text: 'Requested By: ', bold: true }, requestedBy],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: reportName,
            fontSize: 12,
            bold: true,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Date Printed: ', bold: true }, prinDate],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: firstInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
            bold: true,
          },
          {
            text: thirdInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: secondInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
            bold: true,
          },
          {
            text: fourthInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: fifthInfo,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
            bold: true,
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
  return header;
};

const getTableBodyData = (data, cols, paginationArr) => {
  const dataToReturn = [];
  const totaRecordCount = 0;

  const columns = [];
  for (let i = 0; i < cols.length; i += 1) {
    columns.push({
      text: cols[i].headerName,
      fontSize: contentFont,
      border: [true, true, true, true],
      bold: true,
      alignment: cols[i].alignment || 'left',
    });
  }
  dataToReturn.push(columns);
  for (let i = 0; i < data.length; i += 1) {
    const tempCols = [];

    for (let j = 0; j < cols.length; j += 1) {
      let text = data[i][cols[j].field];
      const boolValue = data[i]?.bold || false;
      if (cols[j].isAmount) {
        text = amountFormatter(data[i][cols[j].field]);
      } else if (cols[j].isCount) {
        text = numberFormatter(data[i][cols[j].field]);
      }
      tempCols.push({
        text,
        fontSize: boolValue ? contentFont : contentFont - 1,
        border: [true, true, true, true],
        bold: boolValue,
        alignment: cols[j].alignment || 'left',
        color:
          typeof parseFloat(data[i][cols[j].field]) === 'number' &&
          parseFloat(data[i][cols[j].field]) < 0
            ? '#a81f00'
            : 'black',
      });
    }
    dataToReturn.push(tempCols);
  }
  return dataToReturn;
};
const getTableBody = (data, columns, paginationArr, colWidths, options) => {
  const cols = columns.filter((item) => item.removeColumn !== true);
  const body = [];
  const { titleName = '', mapping = {} } = options;
  const dataKeys = paginationArr.length > 0 ? paginationArr : Object.keys(data);
  const widths =
    colWidths.length > 0
      ? colWidths
      : cols.map((item, i) => (i === 1 ? 160 : '*'));
  //   dataKeys.sort();
  for (let i = 0; i < dataKeys.length; i += 1) {
    if (mapping[dataKeys[i]]) {
      const { labelerCode, drugName, ndc, labelerName } = mapping[dataKeys[i]];
      body.push({
        alignment: 'justify',
        stack: [
          {
            text: `NDC: ${ndc}`,
            bold: true,
            fontSize: contentFont,
          },
          {
            text: `Drug Name: ${drugName}`,
            bold: true,
            fontSize: contentFont,
          },
          {
            text: `Manufacturer ID: ${labelerCode}`,
            bold: true,
            fontSize: contentFont,
          },
          {
            text: `Name: ${labelerName}`,
            bold: true,
            fontSize: contentFont,
          },
        ],
      });
    }
    body.push({
      style: 'tableExample',
      table: {
        widths: ['*'],
        body: [
          [
            {
              style: 'tableExample',
              table: {
                headerRows: 1,
                widths: widths,
                body: getTableBodyData(
                  data[dataKeys[i]],
                  cols,
                  paginationArr,
                  widths
                ),
              },
            },
          ],
        ],
      },
      layout: 'noBorders',
    });
    if (i < dataKeys.length - 1)
      body.push({
        text: '',
        fontSize: 14,
        bold: true,
        pageBreak: 'before',
        margin: [0, 0, 0, 8],
      });
  }
  return body;
};

/**
 * Download as PDF with all Data.
 * @param {object} headerData - conatisn header info of PDF.
 * @param {object} bodyData - constains Body data for PDF.
 */
const getPdfData = (
  bodyData,
  columns,
  headerData,
  paginationArr,
  widths,
  options
) => {
  const { pageSize } = options;
  const content = getTableBody(
    bodyData,
    columns,
    paginationArr,
    widths,
    options
  );
  const currentYear = new Date().getFullYear();
  let docDefinition = {
    header: (currentPage, pageCount) => {
      return getHeader(currentPage, pageCount, headerData, options);
    },
    footer: (currentPage, pageCount) => {
      const footers = [];

      if (
        options.footerVariableObj &&
        options.showFooter &&
        currentPage === pageCount
      ) {
        footers.push({
          text: `${options.footerVariableObj.name}: $${options.footerVariableObj.amount}`,
          fontSize: 10,
          fontFamily: 'arial',
          alignment: 'right',
          marginRight: [25.92],
        });
      }

      footers.push({
        text:`${currentYear} Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.`,
        fontSize: 8,
        color: '#636363',
        fontFamily: 'Arial',
        alignment: 'center',
      });

      return footers;
    },
    content,
    pageMargins,
    styles,
    pageOrientation: 'landscape',
  };

  if (pageSize && pageSize?.width && pageSize.height) {
    docDefinition = {
      ...docDefinition,
      pageSize: {
        width: pageSize.width,
        height: pageSize.height,
      },
    };
  }
  return docDefinition;
};

/**
 * Download as PDF with all Data.
 * @param {object} data - header info of PDF.
 * @param {object} userDefaultValues - use info containg object
 */
const getHeaderData = (data, UserDefaultsValues, logInUser) => {
  const { reportName } = data;
  return {
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    requestedBy:
      UserDefaultsValues && UserDefaultsValues.userId
        ? UserDefaultsValues.userId
        : logInUser,
    reportName,
  };
};

/**
 * Download as PDF with all Data.
 * @param {object} header - header info of PDF.
 * @param {object} bodyData - All the data for the PDF.
 * @param {object} userDefaultValues
 */
export const exportAllPDF = (
  header,
  bodyData,
  columnApi,
  userDefaultValues,
  options = {},
  paginationArr = [],
  widths,
  logInUser
) => {
  const headerData = getHeaderData(header, userDefaultValues, logInUser);
  const colDisplayArray = columnApi.getAllDisplayedColumns()?.map((prop) => {
    return { ...prop, ...prop.colDef };
  });
  const pdfData = getPdfData(
    bodyData,
    colDisplayArray,
    headerData,
    paginationArr,
    widths,
    options
  );
  pdfMake
    .createPdf(pdfData)
    .download(
      `${header?.fileName ? header.fileName : header?.reportName || 'data'}.pdf`
    );
};
